import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "../../lib/utils"
import { buttonVariants } from "src/components/ui/button";

const Pagination = ({
  className,
  ...props
}) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto w-full inline-block max-w-full  px-2 sm:px-4", className)}
    {...props} />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-nowrap items-center justify-center gap-[0.3rem] sm:gap-[2.5rem] overflow-x-auto", className)}
    {...props} />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("flex flex-shrink-0 first:mr-[9px] last:ml-[9px]", className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  onClick,
  disabled,
  ...props
}) => (
  <a
    aria-current={isActive ? "page" : undefined}
    aria-disabled={disabled}
    tabIndex={disabled ? -1 : 0}
    className={cn(buttonVariants({
      variant: isActive ? "outline" : "ghost",
      size,
    }),`md:min-w-[36px] w-[1.5rem] min-w-[37px] text-sm border ${isActive ? "border-primary" : "border-0"}`,
    disabled && "opacity-50 pointer-events-none cursor-not-allowed", className)}
    onClick={disabled ? undefined : onClick}
    {...props} />
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({
  className,
  disabled,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 pl-2 sm:pl-3", className)}
     disabled={disabled}
    {...props}>
    <ChevronLeft className="h-4 w-4" />
    <span>
      <span className="inline sm:hidden">Prev</span>
      <span className="hidden sm:inline">Previous</span>
    </span>
  </PaginationLink>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  disabled,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 pr-2 sm:pr-3", className)}
     disabled={disabled}
    {...props}>
    <span>Next</span>
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
  className,
  ...props
}) => (
  <span
    aria-hidden
    className={cn("flex sm:flex h-9 w-9 items-center justify-center", className)}
    {...props}>
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
